// src/components/TopBar.js
//
// Menubar superior do dashboard. Substituiu os botões flutuantes que
// ficavam no canto inferior direito do SupervisorDash:
//
//   - "Adicionar Agente" / "Conectar Chip Oficial" passaram pra cá,
//     condicionados ao role do usuário + limites do plano (via prop).
//   - Live indicator + clock + breadcrumb são fixos.
//
// É um componente puro de UI: recebe handlers como props pra não acoplar
// com o estado dos modais — quem decide o que cada botão faz é o caller.
import { useEffect, useState } from 'react';
import { Box, Button, ButtonBase, IconButton, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import { FiDownload, FiPlus, FiCpu, FiMenu, FiEdit, FiSun, FiMoon } from 'react-icons/fi';
import CentralNotificacoes from './CentralNotificacoes';
import ConfirmarEmailBanner from './ConfirmarEmailBanner';
import JanelaAtivar from './assinatura/JanelaAtivar';
import useAssinatura from '../hooks/useAssinatura';

// Cores do design v2 — replicadas do mockup HTML pro topbar combinar
// visualmente com o resto da página. Mantenho hardcoded aqui pra não
// criar dependência circular com AgentDash (que define as mesmas vars).
const COLORS = {
  bg2: '#0E1525',
  border: 'rgba(255,255,255,0.07)',
  text: '#DDE4F0',
  text2: '#7A8799',
  text3: '#3E4D60',
  green: '#00D67C',
  greenGlow: 'rgba(0,214,124,0.22)',
  blue: '#5BA4F5',
  bg3: '#131C30',
};

function useClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return now.toLocaleTimeString('pt-BR');
}

// ─── Selo "Período de teste" (out/26) ──────────────────────────────────────
// Substituiu a faixa "Trial ativo · faltam N dias", que ficava em cima da
// TopBar em toda página. Decisão do dono: a contagem regressiva afastava
// quem estava gostando. Agora só um selo discreto, sem número de dias, que
// abre a janela de ativar (AtivarAssinatura). Só pro supervisor — é quem
// ativa; o atendente descobre o teste quando abre uma página fechada.
function SeloTeste({ compacto }) {
  const { emTeste } = useAssinatura();
  const [aberta, setAberta] = useState(false);
  const papel = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}').role; } catch { return null; } })();
  const mostrar = emTeste && papel === 'SUPERVISOR';
  // Depois de cobrar o selo some, mas a janela fica aberta com o "pronto".
  if (!mostrar && !aberta) return null;

  return (
    <>
      {mostrar && <ButtonBase
        onClick={() => setAberta(true)}
        aria-label="Período de teste: ativar a assinatura"
        sx={{
          display: 'inline-flex', alignItems: 'center', gap: 0.8,
          height: 28, px: 1.2, borderRadius: '999px',
          border: `1px solid rgba(0,214,124,0.32)`, bgcolor: 'rgba(0,214,124,0.06)',
          color: COLORS.green, fontFamily: "'Syne', sans-serif", fontSize: '0.7rem', fontWeight: 700,
          letterSpacing: '0.02em', whiteSpace: 'nowrap',
          transition: 'background-color .15s ease, border-color .15s ease',
          '&:hover': { bgcolor: 'rgba(0,214,124,0.12)', borderColor: 'rgba(0,214,124,0.55)' },
          '&.Mui-focusVisible': { outline: `2px solid ${COLORS.green}`, outlineOffset: 2 },
        }}
      >
        <Box component="span" aria-hidden sx={{ width: 6, height: 6, borderRadius: '50%', border: `1.5px solid ${COLORS.green}` }} />
        {compacto ? 'Teste' : 'Período de teste'}
        {!compacto && (
          <Box component="span" sx={{ color: COLORS.text, fontWeight: 600, borderLeft: '1px solid rgba(0,214,124,0.25)', pl: 0.8 }}>
            Ativar
          </Box>
        )}
      </ButtonBase>}
      <JanelaAtivar aberta={aberta} onFechar={() => setAberta(false)} />
    </>
  );
}


/**
 * @param {object} props
 * @param {string} props.crumbBase  Texto à esquerda do breadcrumb ("Visão Geral")
 * @param {string} props.crumbCur   Texto à direita ("Dashboard")
 * @param {boolean} [props.canAddAgent]  Se true, mostra botão "Adicionar Agente"
 * @param {boolean} [props.canAddChip]   Se true, mostra botão "Conectar Chip"
 * @param {() => void} [props.onAddAgent]
 * @param {() => void} [props.onAddChip]
 * @param {() => void} [props.onExport]  Se omitido, botão "Exportar" não renderiza
 * @param {'light'|'dark'} [props.themeMode]  Tema atual da página (só o chat usa hoje)
 * @param {() => void} [props.onToggleTheme]  Se omitido, o botão de tema não renderiza
 */
export default function TopBar({
  crumbBase = 'Visão Geral',
  crumbCur = 'Dashboard',
  canAddAgent = false,
  canAddChip = false,
  onAddAgent,
  onAddChip,
  onExport,
  onNewConversation,  // opcional: só renderiza quando passado (usado no ChatPage).
  onBack,  // opcional: em views como ChatPage mobile, substitui o hambúrguer.
  themeMode,      // opcional: 'light' | 'dark'
  onToggleTheme,  // opcional: só renderiza o botão de tema quando passado
}) {
  const clock = useClock();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <>
    <ConfirmarEmailBanner />
    <Box
      sx={{
        height: 54,
        minHeight: 54,
        bgcolor: COLORS.bg2,
        borderBottom: `1px solid ${COLORS.border}`,
        display: 'flex',
        alignItems: 'center',
        px: { xs: 1.2, md: 3 },
        gap: { xs: 0.8, md: 1.5 },
        position: 'sticky',
        top: 0,
        zIndex: 10,
        fontFamily: "'Syne', sans-serif",
      }}
    >
      {/* Ícone à esquerda no mobile: seta "voltar" se onBack existe, senão
          botão hambúrguer que dispara evento pra sidebar abrir. Em desktop,
          nada — a sidebar já está sempre visível na coluna. */}
      {isMobile && (
        <IconButton
          onClick={() => {
            if (onBack) onBack();
            else window.dispatchEvent(new Event('sidebar-toggle'));
          }}
          size="small"
          sx={{
            color: COLORS.text2,
            width: 34, height: 34,
            border: `1px solid ${COLORS.border}`,
            borderRadius: '7px',
            '&:hover': { color: COLORS.text, bgcolor: 'rgba(255,255,255,0.04)' },
          }}
          aria-label={onBack ? 'Voltar' : 'Abrir menu'}
        >
          {onBack ? <span style={{ fontSize: 16, fontWeight: 700 }}>‹</span> : <FiMenu size={16} />}
        </IconButton>
      )}

      {/* Breadcrumb à esquerda */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flex: 1, minWidth: 0 }}>
        {!isMobile && (
          <>
            <Typography sx={{ fontSize: '0.75rem', color: COLORS.text3, fontFamily: 'inherit' }}>
              {crumbBase}
            </Typography>
            <Typography sx={{ color: COLORS.text3, fontFamily: 'inherit' }}>/</Typography>
          </>
        )}
        <Typography
          sx={{
            fontSize: { xs: '0.85rem', md: '0.95rem' },
            fontWeight: 700, color: COLORS.text, fontFamily: 'inherit',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}
        >
          {crumbCur}
        </Typography>
      </Box>

      {/* Ações à direita */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
        {/* Live indicator + clock */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, mr: 0.5 }}>
          <Box
            sx={{
              width: 7, height: 7, borderRadius: '50%', bgcolor: COLORS.green,
              animation: 'tbPulse 2s infinite',
              '@keyframes tbPulse': {
                '0%, 100%': { boxShadow: `0 0 0 0 ${COLORS.greenGlow}` },
                '50%':      { boxShadow: '0 0 0 5px rgba(0,214,124,0)' },
              }
            }}
          />
          <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: COLORS.green, letterSpacing: '0.06em', fontFamily: 'inherit' }}>
            AO VIVO
          </Typography>
        </Box>
        {!isMobile && (
          <Typography sx={{ fontSize: '0.75rem', color: COLORS.text3, fontFamily: "'Outfit', monospace", mr: 0.5 }}>
            {clock}
          </Typography>
        )}

        <SeloTeste compacto={isMobile} />

        {/* Tema claro/escuro. Ícone só, sem rótulo e sem moldura: é ajuste
            de ambiente, não ação do fluxo — competir com "Nova conversa"
            seria dar a ele um peso que não tem. Só aparece onde o caller
            passa `onToggleTheme` (hoje, o ChatPage). */}
        {onToggleTheme && (
          <Tooltip title={themeMode === 'dark' ? 'Tema claro' : 'Tema escuro'} arrow>
            <IconButton
              onClick={onToggleTheme}
              aria-label={themeMode === 'dark' ? 'Mudar para tema claro' : 'Mudar para tema escuro'}
              size="small"
              sx={{
                color: COLORS.text2,
                p: 0.7,
                borderRadius: '6px',
                '&:hover': { color: COLORS.green, bgcolor: 'rgba(255,255,255,0.05)' },
              }}
            >
              {themeMode === 'dark' ? <FiSun size={15} /> : <FiMoon size={15} />}
            </IconButton>
          </Tooltip>
        )}

        {/* Nova conversa — só renderiza quando o caller passa
            (hoje, apenas o ChatPage). Verde suave pra dar destaque
            como CTA principal do topo do chat. */}
        {onNewConversation && (
          <Button
            startIcon={<FiEdit size={13} />}
            onClick={onNewConversation}
            sx={{
              bgcolor: COLORS.green, color: '#060d14',
              borderRadius: '6px',
              fontFamily: 'inherit',
              fontSize: '0.7rem', fontWeight: 700,
              textTransform: 'none', letterSpacing: '0.02em',
              px: 1.6, py: 0.8,
              '&:hover': { bgcolor: '#00f090' }
            }}
          >
            {isMobile ? 'Nova' : 'Nova conversa'}
          </Button>
        )}

        {/* Exportar — opcional, só renderiza se onExport definido */}
        {onExport && (
          <Button
            startIcon={<FiDownload size={13} />}
            onClick={onExport}
            sx={{
              bgcolor: COLORS.bg3, color: COLORS.text2,
              border: `1px solid ${COLORS.border}`,
              borderRadius: '6px',
              fontFamily: 'inherit',
              fontSize: '0.7rem', fontWeight: 600,
              textTransform: 'none', letterSpacing: '0.02em',
              px: 1.6, py: 0.8,
              '&:hover': { color: COLORS.text, bgcolor: COLORS.bg3 }
            }}
          >
            Exportar
          </Button>
        )}

        {/* Adicionar Agente — só pra supervisor que ainda pode (plan_limits) */}
        {canAddAgent && onAddAgent && (
          <Button
            startIcon={<FiPlus size={13} />}
            onClick={onAddAgent}
            sx={{
              bgcolor: COLORS.green, color: '#060d14',
              borderRadius: '6px',
              fontFamily: 'inherit',
              fontSize: '0.7rem', fontWeight: 700,
              textTransform: 'none', letterSpacing: '0.02em',
              px: 1.6, py: 0.8,
              '&:hover': { bgcolor: '#00f090' }
            }}
          >
            Adicionar Agente
          </Button>
        )}

        {/* Conectar Chip — idem, gated por can_add */}
        {canAddChip && onAddChip && (
          <Button
            startIcon={<FiCpu size={13} />}
            onClick={onAddChip}
            sx={{
              bgcolor: COLORS.bg3, color: COLORS.blue,
              border: '1px solid rgba(91,164,245,0.3)',
              borderRadius: '6px',
              fontFamily: 'inherit',
              fontSize: '0.7rem', fontWeight: 600,
              textTransform: 'none', letterSpacing: '0.02em',
              px: 1.6, py: 0.8,
              '&:hover': { bgcolor: COLORS.bg3, borderColor: 'rgba(91,164,245,0.6)' }
            }}
          >
            Conectar Chip
          </Button>
        )}

        {/* Central de notificações (out/26). Na ponta direita, onde o
            design e a convenção põem o sino. Só com sessão: a TopBar
            também aparece no Register, e lá não há de quem ler aviso. */}
        {localStorage.getItem('token') && <CentralNotificacoes />}
      </Box>
    </Box>
    </>
  );
}
